import { useOrganization } from '@clerk/clerk-react';
import type { ClerkOrganizationPublicMetadata } from '~/config/organizationConfig';
import {
  getOrganizationConfigByFeatureFlags,
  type OrganizationConfig,
} from '~/config/organizationConfig';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';
import { canAccess, isPortfolioOwner } from '~/utils/rbac';

/**
 * Reads feature flags from Clerk organization publicMetadata.
 * Expects organization.publicMetadata.featureFlags to be a string[] (e.g. ['portfolio-management']).
 * When no flags are set: portfolio_owner → [PORTFOLIO_MANAGEMENT];
 * roles with fund access get FUND_MANAGEMENT, with dashboard access get DYNAMIC_DASHBOARD (can have both).
 */
export const useFeatureFlagsFromClerk = (): string[] => {
  const { organization } = useOrganization();
  const metadata = organization?.publicMetadata as
    | ClerkOrganizationPublicMetadata
    | undefined;
  const flags = metadata?.featureFlags ?? [];
  const normalized = Array.isArray(flags) ? flags : [];

  if (normalized.length === 0 && isPortfolioOwner()) {
    return [FEATURE_FLAGS.PORTFOLIO_MANAGEMENT];
  }
  if (normalized.length === 0) {
    const fallbackFlags: string[] = [];
    if (canAccess('fund')) fallbackFlags.push(FEATURE_FLAGS.FUND_MANAGEMENT);
    if (canAccess('dashboard'))
      fallbackFlags.push(FEATURE_FLAGS.DYNAMIC_DASHBOARD);
    if (fallbackFlags.length > 0) return fallbackFlags;
  }
  return normalized;
};

/**
 * Returns the organization config for the current org based on Clerk publicMetadata feature flags.
 * Use this in components instead of passing orgSlug to getOrganizationConfig when you want config driven by Clerk.
 */
export const useOrganizationConfig = (): OrganizationConfig => {
  const featureFlags = useFeatureFlagsFromClerk();
  return getOrganizationConfigByFeatureFlags(featureFlags);
};
